import { ErrorPage } from '@common/ErrorPage'
import { LoadingPage } from '@common/LoadingPage'
import { DetailView } from '@components/enitity'
import { withDetailView } from '@components/enitity/detail/Detail.view'
import { useChangeTicketStatus } from '@services/tickets/mutations/useChangeTicketStatus'
import { useTicketDetail } from '@services/tickets/queries/useTicketDetail'
import type { TicketStatus } from '@services/tickets/types'
import { Building2, CalendarClock, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { Card } from '@/components/ui/card'
import { Combobox } from '@/components/ui/combobox'
import { Label } from '@/components/ui/label'

import {
  AttachmentChips,
  TicketCommentThread
} from '../../components/TicketCommentThread'
import { TicketPriorityPill, TicketStatusPill } from '../../components/TicketPills'
import { TICKET_STATUSES } from '../../constants'
import { useIntlLocale } from '@/i18n/languages'

function TicketsDetailContent() {
  const { id } = useParams()
  const { t } = useTranslation('tickets')
  const { data: ticket, isLoading } = useTicketDetail(id)
  const changeStatus = useChangeTicketStatus()
  const locale = useIntlLocale()

  if (isLoading) return <LoadingPage />
  if (!ticket) return <ErrorPage />

  const tenantName =
    `${ticket.user.first_name ?? ''} ${ticket.user.last_name ?? ''}`.trim() ||
    ticket.user.email

  return (
    <DetailView.Root>
      <DetailView.Breadcrumb
        parentLabel={t('admin.title')}
        parentTo="/admin/tickets"
        current={ticket.title}
      />
      <DetailView.Content>
        <div className="space-y-5">
          <Card className="space-y-4 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="m-0 font-display text-xl font-bold text-ink">
                    {ticket.title}
                  </h2>
                  <TicketStatusPill status={ticket.status} />
                  <TicketPriorityPill priority={ticket.priority} />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-muted-foreground">
                  <Link
                    to={`/admin/users/${ticket.user.id}`}
                    className="inline-flex items-center gap-1.5 text-primary hover:underline"
                  >
                    <User className="h-4 w-4" />
                    {tenantName}
                  </Link>
                  {ticket.flat?.id && (
                    <Link
                      to={`/admin/flats/${ticket.flat.id}`}
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      <Building2 className="h-4 w-4" />
                      {ticket.flat.title}
                    </Link>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarClock className="h-4 w-4" />
                    {new Date(ticket.created_at).toLocaleString(locale)}
                  </span>
                  <span>{t(`categories.${ticket.category}`)}</span>
                </div>
              </div>

              <div className="w-48 shrink-0">
                <Label className="mb-1.5 block">
                  {t('admin.detail.change_status')}
                </Label>
                <Combobox
                  searchable={false}
                  value={ticket.status}
                  disabled={changeStatus.isPending}
                  onChange={(value) => {
                    if (value && value !== ticket.status) {
                      changeStatus.mutate({
                        ticketId: ticket.id,
                        status: value as TicketStatus
                      })
                    }
                  }}
                  options={TICKET_STATUSES.map((value) => ({
                    value,
                    label: t(`statuses.${value}`)
                  }))}
                />
              </div>
            </div>

            {ticket.description && (
              <p className="m-0 whitespace-pre-wrap text-sm leading-relaxed text-ink">
                {ticket.description}
              </p>
            )}

            <AttachmentChips
              ticketId={ticket.id}
              attachments={ticket.attachments}
            />
          </Card>

          <Card className="p-6">
            <TicketCommentThread ticket={ticket} isAdminView />
          </Card>
        </div>
      </DetailView.Content>
    </DetailView.Root>
  )
}

export const TicketsDetail = withDetailView(TicketsDetailContent)
